import type { SessionTokens } from "../types";
import { syncSessionToDesktopHost } from "../lib/host";

const ACCOUNT_ACCESS_KEY = "frogswork_access_token";
const ACCOUNT_REFRESH_KEY = "frogswork_refresh_token";
const DEMO_ACCESS_KEY = "frogswork_demo_access_token";
const DEMO_REFRESH_KEY = "frogswork_demo_refresh_token";

export type SessionKind = "account" | "demo";

function apiBase(): string {
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host === "127.0.0.1";
  const stored = localStorage.getItem("frogswork_api")?.replace(/\/$/, "");
  if (isLocal) {
    if (stored) return stored;
    return "https://api.frogswork.com";
  }
  return stored || "https://api.frogswork.com";
}

function decodeJwtEmail(token: string): string {
  try {
    const [, payloadB64] = token.split(".");
    if (!payloadB64) return "";
    const json = atob(payloadB64.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as { email?: string };
    return typeof payload.email === "string" ? payload.email.trim().toLowerCase() : "";
  } catch {
    return "";
  }
}

export function getSessionKind(): SessionKind | null {
  if (sessionStorage.getItem(DEMO_ACCESS_KEY)) return "demo";
  if (localStorage.getItem(ACCOUNT_ACCESS_KEY)) return "account";
  return null;
}

export function hasActiveDemoSession(): boolean {
  return Boolean(sessionStorage.getItem(DEMO_ACCESS_KEY));
}

export function getAccessToken(): string {
  return sessionStorage.getItem(DEMO_ACCESS_KEY) || localStorage.getItem(ACCOUNT_ACCESS_KEY) || "";
}

export function getRefreshToken(): string {
  return sessionStorage.getItem(DEMO_REFRESH_KEY) || localStorage.getItem(ACCOUNT_REFRESH_KEY) || "";
}

function clearDemoSession(): void {
  sessionStorage.removeItem(DEMO_ACCESS_KEY);
  sessionStorage.removeItem(DEMO_REFRESH_KEY);
}

function clearAccountSession(): void {
  localStorage.removeItem(ACCOUNT_ACCESS_KEY);
  localStorage.removeItem(ACCOUNT_REFRESH_KEY);
}

export function saveSession(tokens: SessionTokens, kind: SessionKind = "account"): void {
  if (kind === "demo") {
    if (tokens.access_token) sessionStorage.setItem(DEMO_ACCESS_KEY, tokens.access_token);
    if (tokens.refresh_token) sessionStorage.setItem(DEMO_REFRESH_KEY, tokens.refresh_token);
    syncSessionToDesktopHost(
      tokens.access_token || getAccessToken(),
      tokens.refresh_token || getRefreshToken()
    );
    return;
  }

  clearDemoSession();
  if (tokens.access_token) localStorage.setItem(ACCOUNT_ACCESS_KEY, tokens.access_token);
  if (tokens.refresh_token) localStorage.setItem(ACCOUNT_REFRESH_KEY, tokens.refresh_token);
  syncSessionToDesktopHost(
    tokens.access_token || getAccessToken(),
    tokens.refresh_token || getRefreshToken()
  );
}

export function clearSession(): void {
  clearDemoSession();
  clearAccountSession();
  syncSessionToDesktopHost("");
}

/** Drop demo-only tokens from persistent storage (legacy sessions). */
export function purgePersistedDemoSession(): void {
  const persisted = localStorage.getItem(ACCOUNT_ACCESS_KEY) || "";
  if (!persisted) return;
  const email = decodeJwtEmail(persisted);
  if (email !== "demo@frogswork.com") return;
  clearAccountSession();
}

/** Leave demo; keep a real account session if one exists. */
export function exitDemoSession(): boolean {
  clearDemoSession();
  syncSessionToDesktopHost(getAccessToken(), getRefreshToken());
  return Boolean(localStorage.getItem(ACCOUNT_ACCESS_KEY));
}

/** If already signed in when the desktop shell loads, mirror tokens for uninstall export. */
export function mirrorSessionToDesktopHost(): void {
  const access = getAccessToken();
  if (!access) return;
  syncSessionToDesktopHost(access, getRefreshToken());
}

function stripAuthParamsFromUrl(): void {
  const url = new URL(window.location.href);
  let changed = false;
  for (const key of ["handoff", "pwa_auth", "access_token", "refresh_token"]) {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  }
  if (changed) {
    const qs = url.searchParams.toString();
    history.replaceState(null, "", url.pathname + (qs ? `?${qs}` : "") + url.hash);
  }
}

/**
 * Capture cross-domain auth from an opaque ?handoff= code (redeemed via API).
 * Strips the code from the URL before analytics or navigation continue.
 */
export async function captureAuthFromUrl(): Promise<boolean> {
  const search = new URLSearchParams(window.location.search);
  const handoff = (search.get("handoff") || "").trim();
  if (!handoff) return false;

  stripAuthParamsFromUrl();
  try {
    const res = await fetch(`${apiBase()}/auth/handoff/redeem`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ code: handoff }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      access_token?: string;
      refresh_token?: string;
      demo?: boolean;
      error?: string;
    };
    if (!res.ok || !data.access_token) {
      return false;
    }
    saveSession(
      {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      },
      data.demo ? "demo" : "account"
    );
    return true;
  } catch {
    return false;
  }
}

export function mapLoginError(message: string): string {
  const msg = String(message || "").trim();
  if (!msg || msg === "Unauthorized") return "Invalid email or password.";
  const lower = msg.toLowerCase();
  if (lower.includes("not active yet") || lower.includes("finish checkout")) {
    return "Finish checkout on frogswork.com to activate your account.";
  }
  return msg;
}
